import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { effectiveAttendanceMode, correctedMode } from "@/lib/attendanceMode";
import MyShiftBlock from "@/components/MyShiftBlock";
import ApprovalQueue from "@/components/ApprovalQueue";
import AbsenceApprovals from "@/components/absences/AbsenceApprovals";
import SubInvoiceApproval from "@/components/SubInvoiceApproval";
import MyAdvancesCard from "@/components/cashbox/MyAdvancesCard";
import { shiftStateFrom, elapsedHm, hoursLabel } from "@/components/mobile/shiftState";
import { buildToday, FILTERS, pragueMin, dayLabel } from "@/components/mobile/todayFeed";
import { HomePrefsButton, useHomePrefs } from "@/components/home/HomePrefs";

// Mobilní přehled majitele / stavbyvedoucího / asistentky (196, varianta B).
// Jen telefon (< 768 px) — počítač dál vidí záložky Osobní / Pracovní.
//   karta směny ↔ karta „Docházka k odsouhlasení“ (táhnutí do strany nebo tečky)
//   4 dlaždice → přepnou filtr osy a dojedou na ni
//   osa „Dnes“ z GET /dashboard/today
// Příchod / odchod dělá tlačítko v dolní liště (194) přes /?shift=1 — obsluhuje to
// MyShiftBlock v režimu headless se stejnou logikou jako dřív (GPS, fotky, výkaz).

const TZ = "Europe/Prague";
const DOT = { dochazka: "hsl(var(--success))", doklady: "hsl(var(--accent-orange))", objednavky: "#3b82c4", denik: "#8b93a7", ukoly: "hsl(var(--foreground))" };
const TONE = { danger: "text-destructive", ok: "text-success", brand: "pp-ink", muted: "text-muted-foreground" };
const reduced = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const todayIso = () => new Date().toLocaleDateString("sv-SE", { timeZone: TZ });

// běžící směna: velký čas se sekundami a lišta „Dnes z 8 h“ — tiká jen tahle část
export function Running({ since, label }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const ms = Math.max(0, now - new Date(since).getTime());
  const hm = elapsedHm(since, now);
  const ss = String(Math.floor((ms % 60000) / 1000)).padStart(2, "0");
  const pct = Math.min(100, Math.max(2, (ms / (8 * 3600000)) * 100));
  const start = new Intl.DateTimeFormat("cs-CZ", { hour: "numeric", minute: "2-digit", timeZone: TZ }).format(new Date(since));
  return (
    <>
      <span className="block">
        <span className="block whitespace-nowrap leading-none font-heading tabular-nums" data-testid="pass-elapsed">
          <span className="text-[56px] font-extrabold">{hm}</span><span className="text-2xl font-bold text-white/80">:{ss}</span>
        </span>
        <span className="block mt-1.5 text-sm text-white/80 truncate">{label} · od {start}</span>
      </span>
      <Bar label="Dnes z 8 h" value={`${hm} h`} pct={pct} color="#ffffff" />
    </>
  );
}

export function Bar({ label, value, pct, color }) {
  return (
    <span className="block w-full">
      <span className="flex justify-between text-[13px] text-white/80"><span>{label}</span><span className="font-bold text-white tabular-nums">{value}</span></span>
      <span className="block h-1.5 rounded-full bg-white/15 mt-2 overflow-hidden"><span className="block h-full rounded-full" style={{ width: `${pct}%`, background: color }} /></span>
    </span>
  );
}

function Avatars({ list, more }) {
  return (
    <span className="flex min-h-[26px]">
      {list.map((a, i) => (
        <span key={a.id} className={`w-[26px] h-[26px] rounded-full grid place-items-center text-[10px] font-bold ring-2 ring-card ${a.me ? "bg-brand text-white" : "bg-success/15 text-success"}`}
          style={{ marginLeft: i ? -4 : 0 }}>{a.ini}</span>
      ))}
      {more > 0 && <span className="w-[26px] h-[26px] rounded-full grid place-items-center text-[10px] font-bold ring-2 ring-card bg-secondary text-muted-foreground" style={{ marginLeft: -4 }}>+{more}</span>}
    </span>
  );
}

function Tile({ label, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="bg-card rounded-[22px] p-3.5 text-left min-h-[116px] min-w-0 flex flex-col justify-between gap-1 active:scale-[.97] transition-transform motion-reduce:transition-none">
      <span className="text-[13px] font-semibold text-muted-foreground truncate">{label}</span>
      {children}
    </button>
  );
}

// jeden řádek osy (mřížka čas | kolejnice | text | částka)
function OsaRow({ e, top, bottom, onOpen }) {
  const dotColor = DOT[e.kind] || DOT.ukoly;
  const aria = [e.t, e.title, e.sub, e.amt, e.pay].filter(Boolean).join(" · ");
  return (
    <button type="button" className="pp-osa" onClick={() => onOpen(e)} aria-label={aria} data-testid={`osa-${e.key}`}>
      <span className={`pp-osa-t ${e.up ? "text-foreground font-semibold" : "text-muted-foreground"}`}>{e.t}</span>
      <span className="pp-osa-rail" aria-hidden="true">
        <span className="pp-osa-line" style={{ top, bottom }} />
        <span className="pp-osa-dot" style={{ background: e.live ? "hsl(var(--success))" : e.up ? "var(--osa-ring)" : dotColor, boxShadow: `0 0 0 3px var(--osa-ring), inset 0 0 0 2px ${dotColor}` }} />
      </span>
      <span className="min-w-0">
        <span className="block text-[15px] font-semibold truncate">{e.title}</span>
        <span className="block text-[13px] text-muted-foreground truncate">{e.sub}</span>
      </span>
      <span className="text-right whitespace-nowrap">
        {e.amt ? <span className={`block text-[15px] font-semibold tabular-nums ${e.amtTone === "ok" ? "text-success" : ""}`}>{e.amt}</span> : null}
        {e.pay ? <span className={`block text-xs font-semibold mt-0.5 ${TONE[e.payTone] || TONE.muted}`}>{e.pay}</span> : null}
      </span>
    </button>
  );
}

export default function ManagerHome() {
  const { user, company } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  // 219: režim docházky z přihlášení hned (dřív až po /daysheet/current — karta směny
  // a její data startovala o jedno kolo později); /me/overview ho případně opraví.
  const [mode, setMode] = useState(() => effectiveAttendanceMode(user, company));
  const [sh, setSh] = useState(null);                 // data z MyShiftBlock (onState)
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [filter, setFilter] = useState("vse");
  const [showAll, setShowAll] = useState(false);
  const [slide, setSlide] = useState(0);
  const [aqOpen, setAqOpen] = useState(false);
  const [aqHi, setAqHi] = useState(null);
  const [aqCount, setAqCount] = useState(null);
  const [aqKey, setAqKey] = useState(0);
  const [nowMin, setNowMin] = useState(() => pragueMin(new Date()) ?? 0);
  const osaRef = useRef(null);
  const chipsRef = useRef(null);
  const swipe = useRef(null);
  const swiped = useRef(false);
  const hiTimer = useRef(null);

  const onShiftState = useCallback((st) => {
    setSh(st);
    setMode((m) => correctedMode(m, st?.ov));
  }, []);

  const load = useCallback(() => {
    api.get("/dashboard/today").then((r) => { setData(r.data); setErr(false); }).catch(() => setErr(true));
  }, []);
  useEffect(() => {
    load();
    const tick = setInterval(() => {
      setNowMin(pragueMin(new Date()) ?? 0);
      if (document.visibilityState === "visible") load();
    }, 60000);
    // telefon odemčený po pauze: hned čerstvá data
    const vis = () => {
      if (document.visibilityState !== "visible") return;
      setNowMin(pragueMin(new Date()) ?? 0); load(); setAqKey((k) => k + 1);
    };
    const changed = () => { load(); setAqKey((k) => k + 1); };
    document.addEventListener("visibilitychange", vis);
    window.addEventListener("pp-shift-changed", changed);
    window.addEventListener("pp-doc-saved", load);
    return () => {
      clearInterval(tick); clearTimeout(hiTimer.current);
      document.removeEventListener("visibilitychange", vis);
      window.removeEventListener("pp-shift-changed", changed);
      window.removeEventListener("pp-doc-saved", load);
    };
  }, [load]);

  const { off } = useHomePrefs();   // 330: Můj Přehled — co si člověk vypnul
  const feed = useMemo(() => buildToday(data, { filter, showAll, nowMin, meId: user?.id, off }), [data, filter, showAll, nowMin, user?.id, off]);
  const mine = data?.scope === "mine";
  const money = !!data?.money;

  const goSlide = (i) => { setSlide(i); if (i === 0) setAqOpen(false); };

  // dlaždice / souhrnný řádek: přepnout filtr a dojet na osu (pod plovoucí horní lištu)
  const jump = (f) => {
    setFilter(f); setShowAll(false);
    setTimeout(() => {
      const el = osaRef.current;
      if (!el) return;
      const hd = document.querySelector('[data-testid="mobile-header"]');
      const off = hd ? hd.getBoundingClientRect().bottom + 8 : 80;
      window.scrollTo({ top: Math.max(0, el.getBoundingClientRect().top + window.scrollY - off), behavior: reduced() ? "auto" : "smooth" });
      const row = chipsRef.current;
      const chip = row?.querySelector('[aria-pressed="true"]');
      if (row && chip) {
        const x = chip.offsetLeft - row.offsetLeft;
        if (x < row.scrollLeft || x + chip.offsetWidth > row.scrollLeft + row.clientWidth - 16) row.scrollTo({ left: Math.max(0, x - 16), behavior: reduced() ? "auto" : "smooth" });
      }
    }, 60);
  };

  // odchod člověka v ose → karta ke schválení, rozbalená, záznam na chvíli zvýrazněný
  const showApproval = (id) => {
    setSlide(1); setAqOpen(true); setAqHi(id);
    clearTimeout(hiTimer.current);
    hiTimer.current = setTimeout(() => setAqHi(null), 2600);
    window.scrollTo({ top: 0, behavior: reduced() ? "auto" : "smooth" });
  };

  const open = (e) => {
    const a = e.act || {};
    if (a.type === "filter") return jump(a.f);
    if (a.type === "task") return nav(`/ukoly/${a.id}`);
    if (a.type === "tasks") return nav("/ukoly");
    if (a.type === "doc") return nav("/ekonomika/doklady");
    if (a.type === "order") return nav(`/objednavky?o=${a.id}`);
    if (a.type === "diary") return nav(a.projectId ? `/zakazky/${a.projectId}?tab=diary` : "/denik");
    if (a.type === "person") {
      const p = a.row;
      if (!p.running && p.approval === "pending") return showApproval(p.id);
      return nav("/dochazka");
    }
    return undefined;
  };

  // ---- karta směny ----
  const ov = sh?.ov, pd = sh?.pd;
  const st = shiftStateFrom(ov, { mode: mode || "site", day: sh?.day, todayHours: sh?.todayHours });
  const working = st.state === "working" && !!st.since;
  const last = pd?.shift?.lastShift;
  const week = Number(pd?.hours?.week) || 0;
  const passChip = st.state === "ended" ? `Hotovo · ${hoursLabel(st.hours)}` : st.state === "draft" ? "Výkaz k vyplnění" : "Mimo práci";
  const passTitle = pd?.shift?.todayPlannedProjectName || last?.projectName || "Moje směna";
  const passSub = last ? `Poslední příchod ${dayLabel(last.date, todayIso())} ${last.from || ""}`.trim() : "Zatím žádná směna";
  const runLabel = ov?.shift?.project_name || (st.mode === "daysheet" ? "Denní výkaz" : "V práci");
  const passClick = () => (st.state === "draft" ? nav("/?shift=1") : nav("/dochazka"));

  const onDown = (e) => { swipe.current = { x: e.clientX, y: e.clientY }; swiped.current = false; };
  const onUp = (e) => {
    const s = swipe.current; swipe.current = null;
    if (!s) return;
    const dx = e.clientX - s.x, dy = e.clientY - s.y;
    if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    swiped.current = true;
    goSlide(dx < 0 ? 1 : 0);
  };
  // po tažení do strany se klepnutí ignoruje (karta se posunula, nemá se nic otevřít)
  const onClickCapture = (e) => { if (swiped.current) { swiped.current = false; e.preventDefault(); e.stopPropagation(); } };

  const hr = Math.floor((pragueMin(new Date()) ?? 600) / 60);
  const greeting = hr < 10 ? "Dobré ráno" : hr < 18 ? "Dobrý den" : "Dobrý večer";
  const dateStr = new Intl.DateTimeFormat("cs-CZ", { weekday: "long", day: "numeric", month: "long", timeZone: TZ }).format(new Date());
  const tiles = feed.tiles;
  const rows = [   // 251: nahoře co bylo, „Teď“, dole co čeká
    ...feed.past.map((e) => ({ type: "ev", e })),
    ...(feed.empty ? [{ type: "empty" }] : []),
    { type: "now" },
    ...feed.live.map((e) => ({ type: "ev", e })),
    ...feed.ups.map((e) => ({ type: "ev", e })),
  ];
  const line = (i) => ({ top: i === 0 ? "10px" : "-8px", bottom: i === rows.length - 1 ? "calc(100% - 10px)" : "-8px" });
  const subinvoiceId = params.get("subinvoice") || null;
  const clearSub = () => { const next = new URLSearchParams(params); next.delete("subinvoice"); setParams(next, { replace: true }); };

  return (
    <div className="-mt-1" data-testid="manager-home">
      <MyShiftBlock mode={mode} headless onState={onShiftState} />

      <h1 className="mx-1 mt-1 font-heading text-[28px] font-extrabold tracking-tight">{greeting}</h1>
      <p className="mx-1 mt-0.5 mb-3.5 text-sm text-muted-foreground truncate">{dateStr}{user?.company_name ? ` · ${user.company_name}` : ""}</p>

      <div className="overflow-hidden -mx-4 px-4" style={{ touchAction: "pan-y" }}
        onPointerDown={onDown} onPointerUp={onUp} onPointerCancel={() => { swipe.current = null; }} onClickCapture={onClickCapture}>
        <div className="pp-track flex items-stretch gap-2.5" style={{ transform: slide ? "translateX(calc(-100% - 10px))" : "none" }}>
          <div className="basis-full shrink-0 min-w-0" aria-hidden={slide !== 0} inert={slide !== 0}>
            <button type="button" onClick={passClick} data-testid="pass-card"
              className={`pp-pass ${working ? "on" : ""} w-full h-full rounded-[26px] px-5 py-[18px] text-left flex flex-col justify-between gap-4 active:scale-[.98] transition-transform motion-reduce:transition-none`}>
              <span className="flex items-center justify-between w-full gap-2">
                <span className="text-sm font-semibold text-white/80">Moje směna</span>
                {working ? (
                  <span className="text-xs font-bold bg-white text-[#1f6b52] rounded-xl px-2.5 py-1 inline-flex items-center gap-1.5"><span className="w-[7px] h-[7px] rounded-full bg-success" />V práci</span>
                ) : (
                  <span className="text-xs font-bold bg-white/15 rounded-xl px-2.5 py-1 whitespace-nowrap" data-testid="pass-chip">{passChip}</span>
                )}
              </span>
              {working ? <Running since={st.since} label={runLabel} /> : (
                <>
                  <span className="block min-w-0">
                    <span className="block font-heading text-[30px] font-extrabold leading-tight truncate">{sh ? passTitle : "…"}</span>
                    <span className="block mt-1.5 text-sm text-white/80 truncate">{sh ? passSub : ""}</span>
                  </span>
                  <Bar label="Tento týden" value={`${String(week).replace(".", ",")} / 40 h`} pct={Math.min(100, (week / 40) * 100)} color="hsl(var(--accent-orange))" />
                </>
              )}
            </button>
          </div>
          <div className="basis-full shrink-0 min-w-0" aria-hidden={slide !== 1} inert={slide !== 1}>
            <ApprovalQueue variant="pass" canSeeMoney={user?.role === "OWNER" || user?.role === "ASSISTANT"}
              expanded={aqOpen} onExpandedChange={setAqOpen} highlightId={aqHi} onCount={setAqCount} reloadKey={aqKey} onChanged={load} />
          </div>
        </div>
      </div>
      <div className="flex justify-center mt-0.5">
        <button type="button" onClick={() => goSlide(0)} aria-label="Moje směna" aria-current={slide === 0} className="w-9 h-7 grid place-items-center" data-testid="pass-dot-0">
          <span className="w-[7px] h-[7px] rounded-full bg-foreground" style={{ opacity: slide === 0 ? 1 : 0.3 }} />
        </button>
        <button type="button" onClick={() => goSlide(1)} aria-current={slide === 1} className="w-9 h-7 grid place-items-center" data-testid="pass-dot-1"
          aria-label={aqCount ? `Docházka k odsouhlasení, ${aqCount}` : "Docházka k odsouhlasení"}>
          <span className={`w-[7px] h-[7px] rounded-full ${aqCount ? "bg-brand" : "bg-foreground"}`} style={{ opacity: slide === 1 || aqCount ? 1 : 0.3 }} />
        </button>
      </div>

      {err && !data ? (
        <div className="mt-2 bg-card rounded-[22px] p-5" data-testid="today-error">
          <p className="text-muted-foreground mb-3">Přehled se nepodařilo načíst.</p>
          <button type="button" onClick={load} className="h-11 px-4 rounded-full bg-secondary font-bold inline-flex items-center gap-2"><RefreshCw size={15} aria-hidden="true" /> Zkusit znovu</button>
        </div>
      ) : !data ? (
        <div className="grid grid-cols-2 gap-2.5 mt-1" data-testid="today-loading">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-[116px] rounded-[22px] bg-card animate-pulse motion-reduce:animate-none" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2.5 mt-1">
            {!off.includes("dochazka") && <Tile label={mine ? "Na mých stavbách" : "V práci teď"} onClick={() => jump("dochazka")} testid="tile-work">
              <span className="font-heading text-[30px] font-extrabold tabular-nums leading-tight whitespace-nowrap">{tiles.work.count}
                {tiles.work.of != null && <span className="text-base text-muted-foreground"> / {tiles.work.of}</span>}</span>
              <Avatars list={tiles.work.avatars} more={tiles.work.more} />
            </Tile>}
            {!off.includes("ukoly") && <Tile label="Úkoly na dnes" onClick={() => jump("ukoly")} testid="tile-tasks">
              <span className="font-heading text-[30px] font-extrabold tabular-nums leading-tight whitespace-nowrap">{tiles.tasks.big}
                {tiles.tasks.open > tiles.tasks.big && <span className="text-base text-muted-foreground" title="Nedokončených úkolů celkem"> z {tiles.tasks.open}</span>}</span>
              <span className={`text-[13px] font-semibold flex items-center gap-1.5 min-w-0 ${TONE[tiles.tasks.tone]}`}>
                <span className="w-[7px] h-[7px] rounded-full bg-current shrink-0" /><span className="truncate">{tiles.tasks.foot}</span></span>
            </Tile>}
            {money ? (!off.includes("doklady") && (
              <Tile label={<><span className="hidden min-[380px]:inline">Faktury po splatnosti</span><span className="min-[380px]:hidden">Po splatnosti</span></>}
                onClick={() => jump("doklady")} testid="tile-invoices">
                <span className="font-heading text-[30px] font-extrabold tabular-nums leading-tight">{tiles.invoices.big}</span>
                <span className={`text-[13px] truncate ${tiles.invoices.big ? "text-muted-foreground" : TONE[tiles.invoices.tone]}`}>{tiles.invoices.foot}</span>
              </Tile>)
            ) : (
              <Tile label="Moje zakázky" onClick={() => nav("/zakazky")} testid="tile-projects">
                <span className="font-heading text-[30px] font-extrabold tabular-nums leading-tight">{tiles.projects.big}</span>
                <span className="text-[13px] text-muted-foreground">v realizaci</span>
              </Tile>
            )}
            {!off.includes("denik") && <Tile label="Stavební deník" onClick={() => jump("denik")} testid="tile-diary">
              <span className="text-lg font-bold leading-tight line-clamp-2">{tiles.diary.title}</span>
              <span className={`text-[13px] font-bold truncate ${TONE[tiles.diary.tone]}`}>{tiles.diary.foot}</span>
            </Tile>}
          </div>

          <div ref={osaRef} className="mt-[22px] flex items-baseline justify-between gap-2 px-1">
            <h2 className="font-heading text-lg font-bold">Dnes</h2>
            <span className="ml-auto text-[13px] font-semibold text-muted-foreground whitespace-nowrap">{mine ? "Moje stavby" : "Celá firma"}</span>
            <HomePrefsButton className="-my-2 -mr-2" />
          </div>
          <div ref={chipsRef} role="group" aria-label="Co ukázat" className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-4 px-4 py-1 mt-1" data-testid="osa-filters">
            {FILTERS.filter(([k]) => !off.includes(k)).map(([k, label]) => {
              const on = feed.filter === k;
              return (
                <button key={k} type="button" aria-pressed={on} onClick={() => { setFilter(k); setShowAll(false); }} data-testid={`osa-filter-${k}`}
                  className={`pp-osa-chip ${on ? "bg-foreground text-background" : "bg-card text-foreground ring-1 ring-inset ring-border"}`}>
                  {k !== "vse" && <span aria-hidden="true" className="w-2 h-2 rounded-full" style={{ background: on && k === "ukoly" ? "hsl(var(--background))" : DOT[k] }} />}
                  {label}
                  {feed.counts?.[k] > 0 && (
                    <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11.5px] font-bold tabular-nums inline-flex items-center justify-center ${on ? "bg-background/20" : "bg-secondary"}`}
                      data-testid={`osa-count-${k}`} aria-label={`${feed.counts[k]} nedokončených`}>{feed.counts[k]}</span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="mt-1.5 bg-card rounded-[22px] py-1.5 pl-1 pr-2" data-testid="osa-feed">
            {(feed.hidden > 0 || feed.canCollapse) && (
              <button type="button" onClick={() => setShowAll((v) => !v)} aria-expanded={showAll} data-testid="osa-more"
                className="block w-full h-11 border-b border-border text-sm font-bold pp-ink">
                {showAll ? "Sbalit" : `Ukázat celý den (+${feed.hidden})`}
              </button>
            )}
            {rows.map((r, i) => {
              const { top, bottom } = line(i);
              if (r.type === "now") {
                return (
                  <div key="now" className="pp-osa py-1.5 pr-1.5" role="note" aria-label={`Teď ${feed.now}`}>
                    <span className="pp-osa-t font-extrabold pp-ink">Teď</span>
                    <span className="pp-osa-rail" aria-hidden="true">
                      <span className="pp-osa-line" style={{ top, bottom }} />
                      <span className="absolute left-[3px] top-[5px] w-3 h-3 rounded-full bg-brand" style={{ boxShadow: "0 0 0 3px hsl(var(--card))" }} />
                    </span>
                    <span className="col-span-2 flex items-center h-[22px]">
                      <span className="flex-1 h-0.5 rounded-full bg-brand opacity-55" /><span className="text-[13px] font-bold tabular-nums pp-ink ml-2">{feed.now}</span>
                    </span>
                  </div>
                );
              }
              if (r.type === "empty") {
                return (
                  <div key="empty" className="pp-osa py-2 pr-1.5" data-testid="osa-empty">
                    <span />
                    <span className="pp-osa-rail" aria-hidden="true"><span className="pp-osa-line" style={{ top, bottom }} /></span>
                    <span className="col-span-2 text-sm text-muted-foreground pt-px pb-1">{feed.empty}</span>
                  </div>
                );
              }
              return <OsaRow key={r.e.key} e={r.e} top={top} bottom={bottom} onOpen={open} />;
            })}
          </div>
        </>
      )}

      {/* ostatní schvalování z Pracovní záložky — samy se schovají, když nic nečeká */}
      <div className="mt-6 space-y-4 empty:hidden">
        <AbsenceApprovals onShow={() => nav("/dochazka?tab=sichtovnice")} />
        <SubInvoiceApproval openId={subinvoiceId} onOpened={clearSub} onChanged={load} />
        <MyAdvancesCard />
      </div>
    </div>
  );
}
